import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import Appbar from 'components/appbar/Appbar';
import { Button } from '@mui/material';
import PatientForm from '../form/PatientForm';
import { usePatientValidation } from '../validation/usePatientValidation';
import PatientTab from '../tab/PatientTab';
import AddressFields from 'components/address/AddressFields';
import { AddressErrors, emptyAddress, validateAddress } from 'helpers/addressValidation';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { useParams } from 'react-router-dom';
import { normalizeDate } from 'helpers/parsePtBRDate';
import InsideLoading from 'components/loading/InsideLoading';
import NoData from 'components/no-data/NoData';
import { format } from 'date-fns';
import PatientObservationForm from '../form/PatientObservationForm';
import PatientContactForm from '../form/PatientContactForm';
import PatientRecordContent from 'pages/patient/record/PatientRecordContent';
import { Address } from 'types/address';
import { Patient } from 'types/patient';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

// Campo com erro de validação -> aba onde ele está
function tabOfField(field: string) {
  return field === 'email' ? 'contact' : 'main';
}

const EditPatient: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation, validate] = usePatientValidation();
  // a ficha é a primeira coisa que se vê ao abrir o paciente
  const [tab, setTab] = useState('record');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedActive, setSavedActive] = useState(true);
  const [patientAddress, setPatientAddress] = useState<Address | null>(null);
  const [clearingAddress, setClearingAddress] = useState(false);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [recordReloadKey, setRecordReloadKey] = useState(0);

  const loadPatient = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get(`/patients/${id}`)
      .then(response => {
        const patientData = response.data;
        setPatientAddress(patientData.address ?? emptyAddress);
        setPatient({
          ...patientData,
          birth_date: patientData.birth_date ? normalizeDate(patientData.birth_date) : null,
        });
        setSavedName(patientData.name);
        setSavedActive(!!patientData.active);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do paciente');
        history.push('/patients');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadPatient().finally(() => setLoading(false));
  }, [loadPatient]);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    const [field] = Object.keys(validation);
    if (field) setTab(tabOfField(field));
  }, [validation]);

  function goBack() {
    history.push('/patients');
  }

  function handleChange<K extends keyof Patient>(index: K, value: Patient[K]) {
    setPatient(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!patient) return;

    setSaving(true);

    const data = {
      ...patient,
      id: patient.id,
      address: patientAddress?.zip_code
        ? {
            zip_code: patientAddress.zip_code,
            street: patientAddress.street,
            neighborhood: patientAddress.neighborhood,
            city: patientAddress.city,
            state: patientAddress.state,
            street_number: patientAddress.street_number,
            complement: patientAddress.complement,
            id: patientAddress.id,
            patient_id: patient.id,
          }
        : null,
      birth_date: patient.birth_date ? format(patient.birth_date, 'yyyy-MM-dd HH:mm:ss') : '',
    };

    api
      .put(`/patients/${patient.id}`, data)
      .then(() => loadPatient())
      .then(() => {
        toast.success('Paciente atualizado');
        setRecordReloadKey(key => key + 1);
        setTab('record');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o paciente');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!patient) return;

    validate(patient)
      .then(() => {
        const errors = validateAddress(patientAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // descarta o que foi digitado e volta para a ficha
  function handleCancel() {
    setValidation({});
    setTab('record');
    setLoading(true);
    loadPatient().finally(() => setLoading(false));
  }

  function handleAddressChange(address: Address) {
    setPatientAddress(address);
    setAddressErrors({});
  }

  function handleClearAddress() {
    if (!patient) return;
    setClearingAddress(true);

    api
      .delete(`/patients/${patient.id}/address`)
      .then(() => {
        setPatientAddress(emptyAddress);
        setAddressErrors({});
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível limpar o endereço do paciente');
      })
      .finally(() => setClearingAddress(false));
  }

  const isEditing = tab !== 'record';

  return (
    <>
      <Appbar title="Pacientes" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title={savedName || 'Paciente'} active={savedActive} backAction={goBack} />

        <WorkArea>
          <PatientTab showRecord onChange={newTab => setTab(newTab)} tab={tab} />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !patient ? (
              <NoData message="Não foi possível carregar os dados do paciente" />
            ) : tab === 'record' ? (
              <PatientRecordContent id={id} reloadKey={recordReloadKey} />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields
                    address={patientAddress}
                    onChange={handleAddressChange}
                    errors={addressErrors}
                    onClear={handleClearAddress}
                    clearing={clearingAddress}
                  />
                ) : tab === 'observation' ? (
                  <PatientObservationForm patient={patient} handleChange={handleChange} />
                ) : tab === 'contact' ? (
                  <PatientContactForm patient={patient} handleChange={handleChange} validation={validation} />
                ) : (
                  <PatientForm type="edit" handleChange={handleChange} patient={patient} validation={validation} />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {isEditing && !loading && patient && (
          <RecordFooter>
            <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
              Cancelar
            </Button>
            <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
              Salvar alterações
            </Button>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditPatient;
